import { fromScaled, toScaled } from '@colplat/protocol';
import { byId, groupBy, type AccountRow, type Ledger } from './rows.js';

/**
 * The report filters, ported rule for rule out of SQL so they run wherever
 * the rows are (ADR-0025).
 *
 * This is the half of the offline requirement that was genuinely missing: the
 * wallets figures were already pure functions over rows, but every filter here
 * was a `WHERE` clause, so a combination nobody had run online could not be
 * answered at all. Every rule below is the same rule the query expressed, and
 * the comments that justified each one are kept with it rather than left
 * behind in the SQL.
 */

export const ACCOUNT_SEPARATOR = ' › ';

export interface ReportFilters {
  from?: string;
  to?: string;
  /** The **transaction** currency, not a split currency — legacy's `all_transactions` filters on the former. */
  currency?: string;
  purpose?: string;
  folder?: string;
  tags?: readonly string[];
  /** `any` shows transactions carrying at least one; `all` only those carrying every one. */
  tagMode?: 'any' | 'all';
}

/**
 * The account types a tag filter counts.
 *
 * Expense and income are where a tagged event's *cost* lands; asset and
 * liability legs are how it was funded. Equity is excluded with them: an
 * opening balance is not something a tag is asked about.
 */
const COUNTED_UNDER_TAG: ReadonlySet<string> = new Set(['EXPENSE', 'INCOME']);

export interface ReportSplit {
  id: string;
  accountId: string;
  memo: string;
  version: number;
  value: string;
  amount: string;
  currencyCode: string;
  accountName: string;
  accountType: string;
  accountPath: string;
  accountLabel: string;
  inScope: boolean;
}

export interface ReportTransaction {
  id: string;
  txDate: string;
  description: string;
  reference: string;
  currencyCode: string;
  seq: number | null;
  version: number;
  splits: ReportSplit[];
}

/** Root-to-leaf name chain per account, with the same cycle guard as everywhere else. */
export function accountChains(accounts: readonly AccountRow[]): Map<string, string[]> {
  const index = byId(accounts);
  const chains = new Map<string, string[]>();
  for (const account of accounts) {
    const chain: string[] = [];
    const seen = new Set<string>();
    let cursor: AccountRow | undefined = account;
    while (cursor && !seen.has(cursor.id)) {
      seen.add(cursor.id);
      chain.unshift(cursor.name);
      cursor = cursor.parentId ? index.get(cursor.parentId) : undefined;
    }
    chains.set(account.id, chain);
  }
  return chains;
}

/**
 * The display label for one leg's account.
 *
 *  - **Inside a folder filter**, an account under the folder is labelled by
 *    the part *below* it. Within "Assets > Current" the thing that tells two
 *    legs apart is `Cash` versus `Bank`, and repeating the folder's own name
 *    on every row costs width without adding information.
 *  - **Otherwise** the last two segments, which separates two accounts that
 *    share a leaf name (every chart of accounts eventually has two things
 *    called "Fees") without printing the whole path.
 */
export function accountLabel(chain: readonly string[], folderChain?: readonly string[]): string {
  if (chain.length === 0) return '';
  if (folderChain && folderChain.length > 0
      && chain.length >= folderChain.length
      && folderChain.every((segment, i) => chain[i] === segment)) {
    const below = chain.slice(folderChain.length);
    return below.length > 0 ? below.join(ACCOUNT_SEPARATOR) : folderChain[folderChain.length - 1]!;
  }
  return chain.slice(-2).join(ACCOUNT_SEPARATOR);
}

/**
 * A folder account and every descendant. Always includes the root, even when
 * the id is unknown, so a caller's membership set is never empty.
 */
export function accountSubtreeIds(accounts: readonly AccountRow[], rootId: string): string[] {
  const children = groupBy(accounts, (a) => a.parentId);
  const out: string[] = [];
  const seen = new Set<string>();
  const stack = [rootId];
  while (stack.length > 0) {
    const id = stack.pop()!;
    if (seen.has(id)) continue;          // a cyclic parent chain must not loop forever
    seen.add(id);
    out.push(id);
    for (const child of children.get(id) ?? []) stack.push(child.id);
  }
  return out;
}

/**
 * Which transactions a filter admits.
 *
 * Every clause is the one the SQL had, including the two that are easy to get
 * backwards:
 *
 *  - **purpose** and **folder** ask whether ANY leg qualifies, not every leg.
 *    A transfer out of a folder belongs in that folder's view, which is the
 *    whole point of it.
 *  - **tagMode `all`** counts DISTINCT matching tags, so a transaction
 *    carrying one chosen tag twice does not satisfy a filter asking for two.
 */
export function selectTransactions(ledger: Ledger, filters: ReportFilters): ReportTransaction[] {
  const accounts = ledger.accounts();
  const accountIndex = byId(accounts);
  const legsByTx = groupBy(ledger.splits(), (s) => s.transactionId);
  const tagsByTx = groupBy(ledger.transactionTags(), (t) => t.transactionId);

  const purposeAccounts = filters.purpose
    ? new Set(ledger.accountPurposes()
      .filter((ap) => ap.purposeId === filters.purpose)
      .map((ap) => ap.accountId))
    : undefined;
  const folderIds = filters.folder
    ? new Set(accountSubtreeIds(accounts, filters.folder))
    : undefined;
  const wantedTags = new Set(filters.tags ?? []);

  const chains = accountChains(accounts);
  const folderChain = filters.folder ? chains.get(filters.folder) : undefined;
  const tagFilterActive = wantedTags.size > 0;

  const kept = ledger.transactions().filter((tx) => {
    if (filters.from && tx.txDate < filters.from) return false;
    if (filters.to && tx.txDate > filters.to) return false;
    if (filters.currency && tx.currencyCode !== filters.currency) return false;

    const legs = legsByTx.get(tx.id) ?? [];
    if (purposeAccounts && !legs.some((s) => purposeAccounts.has(s.accountId))) return false;
    if (folderIds && !legs.some((s) => folderIds.has(s.accountId))) return false;

    if (tagFilterActive) {
      const on = new Set((tagsByTx.get(tx.id) ?? []).map((t) => t.tagId));
      const hits = [...wantedTags].filter((id) => on.has(id)).length;
      if (filters.tagMode === 'all' ? hits < wantedTags.size : hits === 0) return false;
    }
    return true;
  });

  // Newest date first and, within a date, most recently placed first. `seq` is
  // the register's own manual ordering, not a proxy for write order.
  kept.sort((a, b) => b.txDate.localeCompare(a.txDate)
    || (b.seq ?? 0) - (a.seq ?? 0)
    || b.id.localeCompare(a.id));

  return kept.map((tx) => ({
    id: tx.id,
    txDate: tx.txDate,
    description: tx.description ?? '',
    reference: tx.reference ?? '',
    currencyCode: tx.currencyCode,
    seq: tx.seq ?? null,
    version: tx.version ?? 0,
    splits: [...(legsByTx.get(tx.id) ?? [])]
      .sort((a, b) => a.id.localeCompare(b.id))
      .map((s) => {
        const account = accountIndex.get(s.accountId);
        const chain = chains.get(s.accountId) ?? [account?.name ?? '?'];
        /*
         * Every other filter narrows by a property of an *account*, which is
         * what lets it split a transaction's legs into counted and uncounted.
         * A tag belongs to the whole transaction, so it has no such split:
         * counting all of a tagged transaction's legs sums a balanced
         * document and gives zero for every row. It scopes by account *type*
         * instead — the cost side counts, the funding side is greyed — so
         * "tagged renovation" totals what the renovation cost.
         *
         * Filters intersect rather than override, so tag + folder means "the
         * cost legs that are also in this folder". One rule, composed; the
         * alternative is a precedence order nobody can predict from the UI.
         */
        const inScope = (purposeAccounts === undefined || purposeAccounts.has(s.accountId))
          && (folderIds === undefined || folderIds.has(s.accountId))
          && (!tagFilterActive || COUNTED_UNDER_TAG.has(account?.type ?? ''));
        return {
          id: s.id,
          accountId: s.accountId,
          memo: s.memo ?? '',
          version: s.version ?? 0,
          value: s.value,
          amount: s.amount,
          currencyCode: s.currencyCode,
          accountName: account?.name ?? '?',
          accountType: account?.type ?? '',
          accountPath: chain.join(ACCOUNT_SEPARATOR),
          accountLabel: accountLabel(chain, folderChain),
          inScope,
        };
      }),
  }));
}

// ------------------------------------------------------- aggregate reports

export interface TrialBalanceRow {
  accountId: string;
  name: string;
  type: string;
  currencyCode: string;
  /** Decimal text, as stored. Signed: debit-positive, credit-negative, never normalised by account type. */
  balance: string;
}

export interface GeneralLedgerRow {
  accountId: string;
  accountName: string;
  currencyCode: string;
  txDate: string;
  description: string;
  reference: string;
  memo: string;
  amount: string;
}

/** Only the date range applies to the aggregates, exactly as the queries had it. */
function inDateRange(txDate: string, filters: ReportFilters): boolean {
  if (filters.from && txDate < filters.from) return false;
  if (filters.to && txDate > filters.to) return false;
  return true;
}

/**
 * One balance per (account, currency), over every leg in the range.
 *
 * A zero balance is dropped — the query's `HAVING SUM(...) <> 0` — because an
 * account that has netted back to nothing is not part of the trial balance's
 * story, and printing a page of zeroes buries the rows that are.
 */
export function trialBalance(ledger: Ledger, filters: ReportFilters): TrialBalanceRow[] {
  const accounts = byId(ledger.accounts());
  const transactions = byId(ledger.transactions());
  const sums = new Map<string, { row: TrialBalanceRow; total: bigint }>();

  for (const split of ledger.splits()) {
    const tx = transactions.get(split.transactionId);
    const account = accounts.get(split.accountId);
    if (!tx || !account || !inDateRange(tx.txDate, filters)) continue;
    const key = `${account.id}|${split.currencyCode}`;
    const found = sums.get(key);
    const amount = toScaled(split.amount);
    if (found) found.total += amount;
    else {
      sums.set(key, {
        total: amount,
        row: {
          accountId: account.id, name: account.name, type: account.type,
          currencyCode: split.currencyCode, balance: '0',
        },
      });
    }
  }

  return [...sums.values()]
    .filter((s) => s.total !== 0n)
    .map((s) => ({ ...s.row, balance: fromScaled(s.total) }))
    .sort((a, b) => a.currencyCode.localeCompare(b.currencyCode) || a.name.localeCompare(b.name));
}

/** Every leg in the range, grouped for reading by account then currency then date. */
export function generalLedger(ledger: Ledger, filters: ReportFilters): GeneralLedgerRow[] {
  const accounts = byId(ledger.accounts());
  const transactions = byId(ledger.transactions());
  const out: Array<GeneralLedgerRow & { seq: number; splitId: string }> = [];

  for (const split of ledger.splits()) {
    const tx = transactions.get(split.transactionId);
    const account = accounts.get(split.accountId);
    if (!tx || !account || !inDateRange(tx.txDate, filters)) continue;
    out.push({
      accountId: account.id,
      accountName: account.name,
      currencyCode: split.currencyCode,
      txDate: tx.txDate,
      description: tx.description ?? '',
      reference: tx.reference ?? '',
      memo: split.memo ?? '',
      amount: split.amount,
      seq: tx.seq ?? 0,
      splitId: split.id,
    });
  }

  out.sort((a, b) => a.accountName.localeCompare(b.accountName)
    || a.currencyCode.localeCompare(b.currencyCode)
    || a.txDate.localeCompare(b.txDate)
    || a.seq - b.seq
    || a.splitId.localeCompare(b.splitId));

  return out.map(({ seq: _seq, splitId: _splitId, ...row }) => row);
}

/** The transaction currencies actually in use, for the filter's dropdown. */
export function transactionCurrenciesInUse(ledger: Ledger): string[] {
  return [...new Set(ledger.transactions().map((t) => t.currencyCode))].sort();
}
